import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  getEdgeDetails,
  getPersonDetails,
  formatPersonName,
} from '../../../services/networkIntelligence.service';

// Fix leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icon for co-location markers
const coLocationIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background: #06B6D4; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; color: white; font-size: 12px; font-weight: bold;">C</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

/**
 * Component to fit map bounds to markers
 */
const FitBounds = ({ locations }) => {
  const map = useMap();

  useEffect(() => {
    if (locations && locations.length > 0) {
      const bounds = L.latLngBounds(
        locations.map(loc => [loc.latitude, loc.longitude])
      );
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
    }
  }, [locations, map]);

  return null;
};

/**
 * Modal showing detailed information about co-location interactions
 * between two persons, including a map of where they were seen together.
 */
const InteractionsDetailModal = ({
  isOpen,
  onClose,
  person1Id,
  person2Id,
  interactionCount,
}) => {
  const [edgeDetails, setEdgeDetails] = useState(null);
  const [person1, setPerson1] = useState(null);
  const [person2, setPerson2] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && person1Id && person2Id) {
      loadData();
    }
  }, [isOpen, person1Id, person2Id]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load edge details and person info in parallel
      const [details, p1Info, p2Info] = await Promise.all([
        getEdgeDetails(person1Id, person2Id),
        getPersonDetails(person1Id),
        getPersonDetails(person2Id),
      ]);

      if (details) {
        setEdgeDetails(details);
      } else {
        setError('Could not load interaction details');
      }

      setPerson1(p1Info);
      setPerson2(p2Info);
    } catch (err) {
      console.error('Error loading interaction details:', err);
      setError('Failed to load interaction details');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const locations = edgeDetails?.coLocationDetails || [];
  const hasLocations = locations.length > 0;
  const defaultCenter = hasLocations
    ? [locations[0].latitude, locations[0].longitude]
    : [29.76, -95.36]; // Houston default

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-cyan-500 border-b-2 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-cyan-600">
              Co-Location Details
            </h2>
            <p className="text-sm text-gray-500">
              Where {formatPersonName(person1)} and {formatPersonName(person2)} were seen together
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin h-8 w-8 border-4 border-cyan-500 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-gray-500">Loading interaction details...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-cyan-50 rounded-lg p-4">
                  <div className="text-cyan-600 text-sm">Total Co-Locations</div>
                  <div className="text-2xl font-bold text-cyan-700">
                    {edgeDetails?.interactionCount || interactionCount || 0}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-gray-600 text-sm">Connection Strength</div>
                  <div className="text-2xl font-bold">
                    {((edgeDetails?.weight || 0) * 100).toFixed(0)}%
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-gray-600 text-sm">First Seen</div>
                  <div className="text-lg font-medium">
                    {edgeDetails?.firstObserved
                      ? new Date(edgeDetails.firstObserved).toLocaleDateString()
                      : '-'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-gray-600 text-sm">Last Seen</div>
                  <div className="text-lg font-medium">
                    {edgeDetails?.lastObserved
                      ? new Date(edgeDetails.lastObserved).toLocaleDateString()
                      : '-'}
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white rounded-lg border shadow-sm">
                <div className="px-4 py-3 border-b">
                  <h3 className="font-semibold text-gray-900">
                    Co-Location Map ({locations.length} locations)
                  </h3>
                </div>
                <div className="h-80">
                  {hasLocations ? (
                    <MapContainer
                      center={defaultCenter}
                      zoom={12}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <FitBounds locations={locations} />
                      {locations.map((loc, idx) => (
                        <Marker
                          key={idx}
                          position={[loc.latitude, loc.longitude]}
                          icon={coLocationIcon}
                        >
                          <Popup>
                            <div className="text-sm">
                              <div className="font-bold text-cyan-600">
                                Co-Location #{idx + 1}
                              </div>
                              <div className="mt-1">
                                <strong>Date:</strong>{' '}
                                {new Date(loc.timestamp).toLocaleString()}
                              </div>
                              <div>
                                <strong>Location:</strong>{' '}
                                {loc.latitude.toFixed(4)}, {loc.longitude.toFixed(4)}
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MapContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <p>No location data available</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Rebuild the network to include location details
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Location List */}
              {hasLocations && (
                <div className="bg-white rounded-lg border shadow-sm">
                  <div className="px-4 py-3 border-b">
                    <h3 className="font-semibold text-gray-900">Co-Location Timeline</h3>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left">#</th>
                          <th className="px-4 py-2 text-left">Date & Time</th>
                          <th className="px-4 py-2 text-left">Coordinates</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {locations
                          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                          .map((loc, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium text-cyan-600">
                                {idx + 1}
                              </td>
                              <td className="px-4 py-2">
                                {new Date(loc.timestamp).toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-gray-500">
                                {loc.latitude.toFixed(4)}, {loc.longitude.toFixed(4)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InteractionsDetailModal;
